import { useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { UserProfile } from '@shared/types/domain-types'
import { api, STALE_TIMES } from '@/lib/api'
import { getThemeById } from '@/lib/themes'
import type {
  ApiNotificationsSummaryResponse,
  ApiShellBootstrapResponse,
  ApiUserPreferences,
  ApiUserProfile,
} from '@/lib/api/types'
import {
  mapApiNotificationsSummaryToSummaryDto,
  type NotificationsSummaryDto,
} from '@/features/notifications/hooks/use-notifications'
import { normalizeAccessibility, normalizeEnabledModules, normalizeNotificationPreferences } from '@/state/session/normalizers'
import { sessionQueryKeys } from './query-keys'

export interface SharedPreferencesDto {
  enabledModules: ReturnType<typeof normalizeEnabledModules>
  notificationPreferences: ReturnType<typeof normalizeNotificationPreferences>
  accessibility: ReturnType<typeof normalizeAccessibility>
  activeThemeId: string
  activeThemeName: string
}

export interface InventorySummaryDto {
  ownedEmporiumItemIds: string[]
}

function mapApiProfileToSharedProfile(profile: ApiUserProfile): UserProfile {
  const hasPremium = Boolean(profile.isPremium)
  const passDaysRemaining = Math.max(0, Number(profile.passDaysRemaining ?? 0))
  return {
    displayName: profile.displayName || profile.username || 'Aventureiro',
    username: profile.username || 'habitpack',
    avatarUrl: profile.avatarUrl || '',
    annualLevel: profile.level || 1,
    annualXp: profile.xp || 0,
    annualXpToNext: Math.max(1, Number(profile.xpToNextLevel ?? 500)),
    lifetimeLevel: profile.level || 1,
    eternalLevel: profile.eternalLevel || 1,
    habitCoins: profile.coins || profile.habitCoins || 0,
    hasPremium,
    isLifetimePass: hasPremium && passDaysRemaining === 0,
    passDaysRemaining,
  }
}

function mapBootstrapProfileToSharedProfile(payload: ApiShellBootstrapResponse['profileSummary']): UserProfile {
  return {
    displayName: payload.displayName || payload.username || 'Aventureiro',
    username: payload.username || 'habitpack',
    avatarUrl: payload.avatarUrl || '',
    annualLevel: payload.level || 1,
    annualXp: payload.xp || 0,
    annualXpToNext: Math.max(1, Number(payload.xpToNextLevel ?? 500)),
    lifetimeLevel: payload.level || 1,
    eternalLevel: payload.eternalLevel || 1,
    habitCoins: payload.coins || payload.habitCoins || 0,
    hasPremium: Boolean(payload.isPremium),
    isLifetimePass: Boolean(payload.isPremium) && Math.max(0, Number(payload.passDaysRemaining ?? 0)) === 0,
    passDaysRemaining: Math.max(0, Number(payload.passDaysRemaining ?? 0)),
  }
}

function mapApiPreferencesToSharedPreferences(payload: ApiUserPreferences): SharedPreferencesDto {
  const source =
    payload.accessibilityPreferences && typeof payload.accessibilityPreferences === 'object'
      ? (payload.accessibilityPreferences as Record<string, unknown>)
      : {}
  const activeThemeId =
    typeof payload.theme === 'string' && payload.theme.trim().length > 0 ? payload.theme : 'core-protocol'
  return {
    enabledModules: normalizeEnabledModules(source.modules_enabled),
    notificationPreferences: normalizeNotificationPreferences(source.notification_preferences),
    accessibility: normalizeAccessibility(source.accessibility),
    activeThemeId,
    activeThemeName: getThemeById(activeThemeId)?.name ?? 'Core Protocol',
  }
}

function mapBootstrapPreferencesToSharedPreferences(payload: ApiShellBootstrapResponse): SharedPreferencesDto {
  const activeThemeId =
    typeof payload.themeSummary?.activeThemeId === 'string' && payload.themeSummary.activeThemeId.trim().length > 0
      ? payload.themeSummary.activeThemeId
      : 'core-protocol'
  return {
    enabledModules: normalizeEnabledModules(payload.preferencesSummary?.modulesEnabled),
    notificationPreferences: normalizeNotificationPreferences(payload.preferencesSummary?.notificationPreferences),
    accessibility: normalizeAccessibility(payload.preferencesSummary?.accessibility),
    activeThemeId,
    activeThemeName: payload.themeSummary?.activeThemeName?.trim() || getThemeById(activeThemeId)?.name || 'Core Protocol',
  }
}

async function fetchInventorySummary(): Promise<InventorySummaryDto> {
  try {
    const inventoryResponse = await api.get<{ items?: Array<{ itemId?: string | null }> }>('/emporium/inventory')
    const ownedEmporiumItemIds = Array.isArray(inventoryResponse.items)
      ? Array.from(
          new Set(
            inventoryResponse.items
              .map((item) => item.itemId)
              .filter((itemId): itemId is string => typeof itemId === 'string' && itemId.trim().length > 0),
          ),
        )
      : []

    return { ownedEmporiumItemIds }
  } catch (error) {
    console.error('[fetchInventorySummary] API error:', error instanceof Error ? error.message : 'Unknown error')
    return { ownedEmporiumItemIds: [] }
  }
}

export async function fetchSharedProfile(): Promise<UserProfile> {
  try {
    const profile = await api.get<ApiUserProfile>('/users/profile')
    return mapApiProfileToSharedProfile(profile)
  } catch (error) {
    console.error('[fetchSharedProfile] API error:', error instanceof Error ? error.message : 'Unknown error')
    throw error
  }
}

export async function fetchSharedPreferences(): Promise<SharedPreferencesDto> {
  try {
    const preferences = await api.get<ApiUserPreferences>('/users/preferences')
    return mapApiPreferencesToSharedPreferences(preferences)
  } catch (error) {
    console.error('[fetchSharedPreferences] API error:', error instanceof Error ? error.message : 'Unknown error')
    throw error
  }
}

export async function fetchNotificationsSummary(): Promise<NotificationsSummaryDto> {
  try {
    const payload = await api.get<ApiNotificationsSummaryResponse>('/notifications/summary')
    return mapApiNotificationsSummaryToSummaryDto(payload)
  } catch (error) {
    console.error('[fetchNotificationsSummary] API error:', error instanceof Error ? error.message : 'Unknown error')
    throw error
  }
}

export function seedShellCachesFromBootstrap(
  queryClient: QueryClient,
  userId: string,
  bootstrap: ApiShellBootstrapResponse,
): void {
  queryClient.setQueryData(sessionQueryKeys.profile(userId), mapBootstrapProfileToSharedProfile(bootstrap.profileSummary))
  queryClient.setQueryData(sessionQueryKeys.preferences(userId), mapBootstrapPreferencesToSharedPreferences(bootstrap))
  queryClient.setQueryData(
    sessionQueryKeys.notificationsSummary(userId),
    mapApiNotificationsSummaryToSummaryDto(bootstrap.notificationsSummary),
  )
  queryClient.setQueryData(sessionQueryKeys.inventorySummary(userId), {
    ownedEmporiumItemIds: Array.isArray(bootstrap.inventorySummary?.ownedEmporiumItemIds)
      ? Array.from(
          new Set(
            bootstrap.inventorySummary!.ownedEmporiumItemIds.filter(
              (itemId): itemId is string => typeof itemId === 'string' && itemId.trim().length > 0,
            ),
          ),
        )
      : [],
  } satisfies InventorySummaryDto)
}

export function readShellCacheSnapshot(queryClient: QueryClient, userId: string | null | undefined) {
  return {
    profile: queryClient.getQueryData<UserProfile>(sessionQueryKeys.profile(userId)) ?? null,
    preferences: queryClient.getQueryData<SharedPreferencesDto>(sessionQueryKeys.preferences(userId)) ?? null,
    notificationsSummary:
      queryClient.getQueryData<NotificationsSummaryDto>(sessionQueryKeys.notificationsSummary(userId)) ?? null,
    inventorySummary: queryClient.getQueryData<InventorySummaryDto>(sessionQueryKeys.inventorySummary(userId)) ?? null,
  }
}

export async function refreshSharedProfile(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return null
  return queryClient.fetchQuery({
    queryKey: sessionQueryKeys.profile(userId),
    queryFn: fetchSharedProfile,
    staleTime: STALE_TIMES.NORMAL,
  })
}

export async function refreshSharedPreferences(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return null
  return queryClient.fetchQuery({
    queryKey: sessionQueryKeys.preferences(userId),
    queryFn: fetchSharedPreferences,
    staleTime: STALE_TIMES.NORMAL,
  })
}

export async function refreshInventorySummary(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return null
  return queryClient.fetchQuery({
    queryKey: sessionQueryKeys.inventorySummary(userId),
    queryFn: fetchInventorySummary,
    staleTime: STALE_TIMES.NORMAL,
  })
}

export async function invalidateProfileDomain(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return
  await refreshSharedProfile(queryClient, userId)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['manage'] }),
    queryClient.invalidateQueries({ queryKey: ['arena'] }),
    queryClient.invalidateQueries({ queryKey: ['social'] }),
    queryClient.invalidateQueries({ queryKey: ['emporium', 'pass'] }),
  ])
}

export async function invalidatePreferencesDomain(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return
  await refreshSharedPreferences(queryClient, userId)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['settings'] }),
    queryClient.invalidateQueries({ queryKey: ['manage', 'modules'] }),
  ])
}

export async function invalidateNotificationsDomain(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: sessionQueryKeys.notificationsSummary(userId) }),
    queryClient.invalidateQueries({ queryKey: ['session', 'notifications-list', userId] }),
  ])
}

export async function invalidateInventoryDomain(queryClient: QueryClient, userId: string | null | undefined) {
  if (!userId) return
  await refreshInventorySummary(queryClient, userId)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['user-themes', userId] }),
    queryClient.invalidateQueries({ queryKey: ['emporium', 'page'] }),
    queryClient.invalidateQueries({ queryKey: ['emporium', 'inventory'] }),
    queryClient.invalidateQueries({ queryKey: ['emporium', 'owned-items'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])
}

export function useSharedProfile(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: sessionQueryKeys.profile(userId),
    queryFn: fetchSharedProfile,
    enabled: Boolean(userId) && enabled,
    staleTime: STALE_TIMES.NORMAL,
  })
}

export function useSharedPreferences(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: sessionQueryKeys.preferences(userId),
    queryFn: fetchSharedPreferences,
    enabled: Boolean(userId) && enabled,
    staleTime: STALE_TIMES.NORMAL,
  })
}

export function useInventorySummary(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: sessionQueryKeys.inventorySummary(userId),
    queryFn: fetchInventorySummary,
    enabled: Boolean(userId) && enabled,
    staleTime: STALE_TIMES.NORMAL,
  })
}
